import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { langPrefixFromPath } from '../siteRoutes';
import { ArrowLeft, X, Loader2 } from 'lucide-react';
import { useHotel } from '../context/HotelContext';
import { useLanguage } from '../context/LanguageContext';
import PriceMatch from './PriceMatch';
import ReviewHighlights from './ReviewHighlights';
import BeachNowStrip from './BeachNowStrip';
import WhatToExpect from './WhatToExpect';
import GuestsAsked from './GuestsAsked';
import Header from './Header';
import Footer from './Footer';
import SEOHead, { generateRoomSchema } from './SEOHead';
import { getRoomMeta } from '../i18n/seo';
import OptimizedImage from './OptimizedImage';
import { ImageLightbox } from './rooms/ImageLightbox';
import { BookingSidebar } from './rooms/BookingSidebar';
import { BookingSummaryModal } from './rooms/BookingSummaryModal';
import { RoomPulse, recordRoomView } from './rooms/RoomPulse';
import { HalalBadge } from './HalalBadge';
import { trackRoomView, trackBookingClick, trackBookModalOpened, trackBookConfirmed } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RoomDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hotelInfo, getRoomById, loading: dataLoading } = useHotel();
  const { lang, t } = useLanguage();
  const { pathname } = useLocation();
  const [currentImage, setCurrentImage] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [bookingData, setBookingData] = useState(null);
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [voucher, setVoucher] = useState('');

  const room = getRoomById(id);

  useEffect(() => {
    if (id) { trackRoomView(parseInt(id)); recordRoomView(parseInt(id)); }
    window.scrollTo(0, 0);
  }, [id]);

  // Load booking data from localStorage (set by <Rooms/>). Only trust
  // entries younger than 1 hour so stale searches don't confuse the sidebar.
  useEffect(() => {
    const saved = localStorage.getItem('bookingData');
    if (!saved) return;
    try {
      const data = JSON.parse(saved);
      if (Date.now() - data.timestamp < 3600000) setBookingData(data);
    } catch (e) {
      console.error('Error parsing booking data:', e);
    }
  }, []);

  // Fetch pricing whenever booking data + room are ready.
  useEffect(() => {
    if (!bookingData || !room) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await fetch(
          `${API_URL}/api/beds24/availability?check_in=${bookingData.checkIn}&check_out=${bookingData.checkOut}&adults=${bookingData.adults}&children=${bookingData.children}`
        );
        const data = await r.json();
        if (!cancelled && data.success) {
          const match = data.rooms.find((rr) => rr.local_room_id === room.id);
          if (match) setPricing(match);
        }
      } catch (e) {
        console.error('Pricing error:', e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [bookingData, room]);

  if (dataLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 size={40} className="animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading room details...</p>
        </div>
      </div>
    );
  }

  if (!room) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Room not found</h1>
          <Link to="/" className="text-orange-500 hover:underline">Back to Home</Link>
        </div>
      </div>
    );
  }

  const images = room.images || [];

  const openLightbox = (index) => {
    setCurrentImage(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const openBookingModal = (code) => {
    setVoucher(typeof code === 'string' ? code : '');
    setShowBookingModal(true);
    document.body.style.overflow = 'hidden';
    trackBookModalOpened(parseInt(id));
  };

  const closeBookingModal = () => {
    setShowBookingModal(false);
    document.body.style.overflow = 'auto';
  };

  const handleCompleteBooking = () => {
    trackBookingClick(parseInt(id));
    trackBookConfirmed(parseInt(id));
    let url = `${langPrefixFromPath(pathname)}/booking?propid=${hotelInfo?.beds24Id || '231163'}`;
    if (bookingData) {
      url += `&checkin=${bookingData.checkIn}&checkout=${bookingData.checkOut}`;
      url += `&adults=${bookingData.adults}&children=${bookingData.children}`;
    }
    if (room?.beds24Id) url += `&roomId=${room.beds24Id}`;
    if (voucher) url += `&voucher=${encodeURIComponent(voucher)}`;
    closeBookingModal();
    navigate(url);
  };

  const thumbnails = images.map((img, i) => (
    <button
      key={i}
      onClick={() => setCurrentImage(i)}
      className={'aspect-square rounded-lg overflow-hidden transition-all ' + (i === currentImage ? 'ring-2 ring-orange-500 ring-offset-2' : 'opacity-70 hover:opacity-100')}
    >
      <img src={img} alt={'Thumbnail ' + (i + 1)} className="w-full h-full object-cover" loading="lazy" decoding="async" />
    </button>
  ));

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={getRoomMeta(lang, room).title}
        description={getRoomMeta(lang, room).description}
        image={room.images?.[0]}
        url={`/room/${room.id}`}
        keywords={`${room.name}, Ulcinj hotel room, ${room.amenities?.join(', ')}, Montenegro accommodation`}
        schema={[generateRoomSchema(room, lang), {
          '@context': 'https://schema.org', '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Hotel La Rosa Negra', item: `https://larosa-negra.com${lang === 'en' ? '' : `/${lang}`}/` },
            { '@type': 'ListItem', position: 2, name: t('nav.rooms'), item: `https://larosa-negra.com${lang === 'en' ? '' : `/${lang}`}/rooms` },
            { '@type': 'ListItem', position: 3, name: room.name, item: `https://larosa-negra.com${lang === 'en' ? '' : `/${lang}`}/room/${room.id}` },
          ],
        }]}
      />

      <Header />

      <div className="bg-white border-b shadow-sm mt-[120px] lg:mt-[100px]">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/#rooms" className="inline-flex items-center text-orange-500 hover:text-orange-600 font-medium bg-orange-50 px-4 py-2 rounded-lg border border-orange-200 transition-colors">
            <ArrowLeft size={20} className="mr-2" />
            Back to All Rooms
          </Link>
          <Link to="/" className="flex items-center gap-2 text-gray-500 hover:text-gray-700 font-medium px-4 py-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={20} />
            Close
          </Link>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <HalalBadge variant="panel" className="mb-3" />
            <RoomPulse roomId={room.id} className="mb-4" />
            <div className="mb-6"><PriceMatch roomId={room.id} /></div>
            <div className="mb-4"><ReviewHighlights /></div>
            <div className="mb-4"><BeachNowStrip compact /></div>
            <div className="mb-6 rounded-2xl border border-gray-200 p-5"><WhatToExpect compact /></div>
            <div
              className="relative rounded-2xl overflow-hidden shadow-lg mb-4 cursor-pointer group"
              onClick={() => openLightbox(currentImage)}
              data-testid="room-detail-hero"
            >
              <OptimizedImage
                src={images[currentImage]}
                alt={room.name + ' - Photo ' + (currentImage + 1)}
                className="w-full h-[400px] md:h-[500px] object-cover transition-transform duration-300 group-hover:scale-105"
              />
              <div className="absolute bottom-4 left-4 bg-black/60 text-white px-4 py-2 rounded-full text-sm font-semibold">
                {currentImage + 1} / {images.length} - Click to enlarge
              </div>
            </div>
            <div className="grid grid-cols-6 gap-2">{thumbnails}</div>
            <GuestsAsked compact roomId={room.id} limit={5} className="mt-6" />
          </div>

          <div className="lg:col-span-1">
            <BookingSidebar
              room={room}
              pricing={pricing}
              bookingData={bookingData}
              loading={loading}
              onBook={openBookingModal}
            />
          </div>
        </div>
      </div>

      <Footer />

      <ImageLightbox
        open={lightboxOpen}
        images={images}
        currentIndex={currentImage}
        onIndexChange={setCurrentImage}
        onClose={closeLightbox}
        alt={room.name}
      />

      <BookingSummaryModal
        open={showBookingModal}
        onClose={closeBookingModal}
        onConfirm={handleCompleteBooking}
        room={{ ...room, pricing }}
        bookingSummary={bookingData}
        title="Finalize Your Reservation"
        confirmTestId="room-detail-complete-booking"
      />
    </div>
  );
};

export default RoomDetail;
